import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ReactGA from 'react-ga4';

// Styled Components
const LandingContainer = styled.div`
  font-family: 'Poppins', sans-serif;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  background: linear-gradient(to right, #2D3142 70%, #E94057 100%);
  color: white;
  padding: 6rem 0;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 4rem 0;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  max-width: 650px;
  
  @media (max-width: 768px) {
    margin: 0 auto;
  }
`;

const BoldBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid var(--primary);
`;

const Heading = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: white;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  span {
    color: var(--primary);
    position: relative;
  }
`;

const SubHeading = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2rem;
  line-height: 1.6;
  font-weight: 300;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background-color: var(--primary);
  color: white;
  font-weight: 700;
  padding: 1.25rem 2.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    background-color: white;
    color: var(--primary);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const GiftTag = styled.div`
  position: absolute;
  top: -15px;
  right: -15px;
  background-color: #FFAA8A;
  color: #2D3142;
  font-weight: 700;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  transform: rotate(5deg);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  font-size: 1rem;
`;

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 2rem;
`;

const HeroImageContainer = styled.div`
  position: relative;
  
  @media (max-width: 768px) {
    margin-top: 3rem;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transform: perspective(1000px) rotateY(-5deg);
  transition: transform 0.5s ease;
  
  &:hover {
    transform: perspective(1000px) rotateY(0);
  }
`;

const FeaturesSection = styled.section`
  padding: 6rem 0;
  background-color: white;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: #2D3142;
  text-align: center;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background-color: var(--primary);
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #6C757D;
  margin: 2rem auto 3rem;
  text-align: center;
  max-width: 800px;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 2.5rem 2rem;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
  
  .icon {
    width: 70px;
    height: 70px;
    background-color: ${props => props.iconBg || 'rgba(233, 64, 87, 0.1)'};
    color: ${props => props.iconColor || 'var(--primary)'};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    font-size: 2rem;
  }
  
  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #2D3142;
  }
  
  p {
    color: #6C757D;
    margin-bottom: 0;
    line-height: 1.6;
  }
`;

const HowItWorksSection = styled.section`
  padding: 6rem 0;
  background-color: #f8f9fa;
`;

const StepCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  min-width: 60px;
  background: linear-gradient(135deg, var(--primary) 0%, #F27121 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  margin-right: 2rem;
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 1.5rem;
    margin-left: auto;
    margin-right: auto;
  }
`;

const StepContent = styled.div``;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: #2D3142;
`;

const StepDescription = styled.p`
  color: #6C757D;
  margin-bottom: 0;
  line-height: 1.6;
`;

const CTASection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, #E94057 0%, #F27121 100%);
  color: white;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  
  span {
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 8px;
      background-color: rgba(255, 255, 255, 0.3);
      z-index: -1;
    }
  }
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
`;

const CTAButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: white;
  color: var(--primary);
  font-weight: 700;
  padding: 1.25rem 2.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    background-color: #2D3142;
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const GiftLandingPage = () => {
  // Google Analytics tracking code setup
  useEffect(() => {
    // Track page view for this landing page
    ReactGA.send({
      hitType: "pageview",
      page: "/gift-landing",
      title: "Gift Landing Page"
    });

    // Event tracking for CTA clicks
    const trackCTAClick = () => {
      ReactGA.event({
        category: 'engagement',
        action: 'gift_cta_click',
        label: 'Gift Landing Page CTA',
        value: 1
      });
    };
    // Add event listeners to CTA buttons
    const ctaButtons = document.querySelectorAll('.gift-cta-button');
    ctaButtons.forEach(button => {
      button.addEventListener('click', trackCTAClick);
    });

    return () => {
      // Clean up event listeners
      ctaButtons.forEach(button => {
        button.removeEventListener('click', trackCTAClick);
      });
    };
  }, []);

  return (
    <LandingContainer>
      <HeroSection>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <HeroContent>
                <BoldBadge>
                  <i className="bi bi-gift me-2"></i>
                  THE PERFECT GIFT FOR NEW PARENTS
                </BoldBadge>
                <Heading>
                  GIVE THE <span>GIFT OF SLEEP</span> TO A FAMILY YOU CARE ABOUT
                </Heading>
                <SubHeading>
                  Help a family you love get the rest they deserve with a personalized sleep plan created using AI and evidence-based guidelines from trusted Australian resources.
                </SubHeading>
                <ButtonWrapper>
                  <CtaButton to="/gift" className="gift-cta-button">
                    GIVE A SLEEP PLAN
                  </CtaButton>
                </ButtonWrapper>
              </HeroContent>
            </div>
            <div className="col-lg-5">
              <HeroImageContainer>
                <HeroImage 
                  src="/images/jonathan-borba-RWgE9_lKj_Y-unsplash.jpg" 
                  alt="Peaceful sleeping baby gift" 
                />
              </HeroImageContainer>
            </div>
          </div>
        </div>
      </HeroSection>

      <FeaturesSection>
        <div className="container">
          <SectionTitle>WHY GIVE THE GIFT OF SLEEP?</SectionTitle>
          <SectionSubtitle>
            A personalized sleep plan is a thoughtful and practical gift that can make a real difference in the lives of new parents.
          </SectionSubtitle>
          
          <FeaturesGrid>
            <FeatureCard iconBg="rgba(233, 64, 87, 0.1)" iconColor="var(--primary)">
              <div className="icon">
                <i className="bi bi-heart"></i>
              </div>
              <h3>Thoughtful & Unique</h3>
              <p>A truly meaningful gift that shows you care about their wellbeing and understand the challenges of parenthood.</p>
            </FeatureCard>
            
            <FeatureCard iconBg="rgba(126, 203, 185, 0.1)" iconColor="var(--secondary)">
              <div className="icon">
                <i className="bi bi-lightbulb"></i>
              </div>
              <h3>Personalized Solution</h3>
              <p>Not just another baby product, but a customized plan created specifically for their child's unique needs.</p>
            </FeatureCard>
            
            <FeatureCard iconBg="rgba(139, 110, 180, 0.1)" iconColor="var(--purple)">
              <div className="icon">
                <i className="bi bi-stars"></i>
              </div>
              <h3>Long-lasting Impact</h3>
              <p>Better sleep can transform family life, making this a gift that continues to benefit them for months to come.</p>
            </FeatureCard>
          </FeaturesGrid>
        </div>
      </FeaturesSection>

      <HowItWorksSection>
        <div className="container">
          <SectionTitle>HOW IT WORKS</SectionTitle>
          <SectionSubtitle>
            Giving the gift of sleep is simple and thoughtful. Here's how it works:
          </SectionSubtitle>
          
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <StepCard>
                <StepNumber>1</StepNumber>
                <StepContent>
                  <StepTitle>Purchase a Gift Plan</StepTitle>
                  <StepDescription>
                    Choose between a single child plan ($49) or a family plan for two children ($79). Complete your purchase securely online.
                  </StepDescription>
                </StepContent>
              </StepCard>
              
              <StepCard>
                <StepNumber>2</StepNumber>
                <StepContent>
                  <StepTitle>Personalize Your Gift</StepTitle>
                  <StepDescription>
                    Add a personal message, the recipient's email address, and our system will send the gift immediately.
                  </StepDescription>
                </StepContent>
              </StepCard>
              
              <StepCard>
                <StepNumber>3</StepNumber>
                <StepContent>
                  <StepTitle>They Receive Their Gift</StepTitle>
                  <StepDescription>
                    The recipient gets an email with your message and instructions on how to create their personalized sleep plan.
                  </StepDescription>
                </StepContent>
              </StepCard>
              
              <StepCard>
                <StepNumber>4</StepNumber>
                <StepContent>
                  <StepTitle>They Create Their Sleep Plan</StepTitle>
                  <StepDescription>
                    They'll answer questions about their child, and our AI will create a personalized sleep plan based on their responses and evidence-based guidelines.
                  </StepDescription>
                </StepContent>
              </StepCard>
            </div>
          </div>
        </div>
      </HowItWorksSection>

      <CTASection>
        <div className="container">
          <CTATitle>
            GIVE THE <span>GIFT OF SLEEP</span> TODAY
          </CTATitle>
          <CTADescription>
            Help a family you care about get the rest they deserve with a thoughtful, practical gift that keeps on giving night after night.
          </CTADescription>
          <CTAButtonWrapper>
            <CTAButton to="/gift" className="gift-cta-button">
              PURCHASE A GIFT PLAN
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </CTASection>
    </LandingContainer>
  );
};

export default GiftLandingPage;
